import type { UpgradeContext } from '@tabbio/types';

type Translate = (
  key: string,
  options?: Record<string, unknown>,
) => string;

export type UpgradeReasonKey =
  | 'credits'
  | 'image'
  | 'research'
  | 'document'
  | 'edit'
  | 'automations';

/**
 * Fair use (2026-09-25): which "why am I seeing this" line the paywall shows
 * for a blocked action. Credits ran out, a daily capability sample or quota
 * was used, or automations need Pro. Null for a paywall the user opened.
 */
export function getUpgradeReasonKey(
  context: UpgradeContext | null | undefined,
): UpgradeReasonKey | null {
  if (!context?.reason) return null;
  switch (context.reason) {
    case 'ai_daily_limit_reached':
    case 'workspace_ai_limit_reached':
      return 'credits';
    case 'artifact_edit_quota_reached':
      return 'edit';
    case 'capability_quota_reached':
      switch (context.actionKey) {
        case 'individual.image_generate':
          return 'image';
        case 'individual.web_research':
          return 'research';
        case 'individual.document_render':
          return 'document';
        case 'individual.artifact_edit':
          return 'edit';
        default:
          return 'credits';
      }
    case 'wrong_tier':
      return context.featureKey === 'individual.automations' ||
        context.placement === 'nova_activate' ||
        context.placement === 'nova_run'
        ? 'automations'
        : null;
    default:
      return null;
  }
}

/**
 * The line above the plans: the included allowance left when the action has
 * one, otherwise why the paywall opened (`getUpgradeReasonKey`).
 */
export function getUpgradeAllowanceCopy(
  context: UpgradeContext | null | undefined,
  t: Translate,
  locale: string,
) {
  if (
    context?.allowanceLimit === undefined ||
    context.allowanceRemaining === undefined ||
    !context.allowanceUnit
  ) {
    const reason = getUpgradeReasonKey(context);
    return reason
      ? t(`upgrade.context.reason.${reason}`, {
          plan: context?.recommendedTier === 'pro' ? 'Pro' : 'Plus',
        })
      : null;
  }

  const remaining = Math.max(context.allowanceRemaining, 0);
  const limit = Math.max(context.allowanceLimit, 0);
  const unit = t(`upgrade.context.units.${context.allowanceUnit}`);
  const usage = t(
    remaining > 0
      ? 'upgrade.context.allowanceRemaining'
      : 'upgrade.context.allowanceUsed',
    { remaining, limit, unit },
  );

  if (!context.resetAt) {
    return usage;
  }

  const resetDate = new Date(context.resetAt);
  if (Number.isNaN(resetDate.getTime())) {
    return usage;
  }

  const date = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(resetDate);

  return `${usage} · ${t('upgrade.context.resetsOn', { date })}`;
}
